import { useRef } from 'react'
import { useStore } from '@/store'
import StreamTable from '@/components/table/StreamTable'
import WorldDetailTable from '@/components/table/WorldDetailTable'
import HexDetailTable from '@/components/table/HexDetailTable'
import VizPanel from '@/routing/VizPanel'
import { useElementHeight } from '@/hooks/useElementHeight'
import { useResizableTable } from '@/hooks/useResizableTable'
import {
  computeSplit,
  DIVIDER_H,
  MIN_VIZ_H,
  MIN_TABLE_H,
} from '@/lib/layout/splitMath'

// The viz pane on top, a draggable divider in the middle, and the optional
// stream table at the bottom. When the table is dragged taller than will fit,
// it overlaps the viz pane with a shadow rather than squashing the viz below
// its minimum height.
export default function VizTableSplit() {
  const view        = useStore(s => s.view)
  const tableOpen   = useStore(s => s.tableOpen)
  const tableH      = useStore(s => s.tableH)
  const toggleTable = useStore(s => s.toggleTable)

  const hostRef = useRef<HTMLDivElement | null>(null)
  const splitH = useElementHeight(hostRef)
  // Deep Dive embeds its own tables; World/Hex keep the split pane.
  const deepOnly = view === 'deep'
  const showTable = !deepOnly && tableOpen
  const { vizH, overlapPx } = computeSplit(splitH, tableH, showTable)

  const drag = useResizableTable(hostRef)

  // Chart-mirror detail table: World hops, Hex tiles (all modes via useHexItems), else StreamTable.
  const detailTable =
    view === 'world' ? <WorldDetailTable />
      : view === 'hex' ? <HexDetailTable />
        : <StreamTable />

  if (deepOnly) {
    return (
      <div
        ref={hostRef}
        style={{
          display: 'flex', flexDirection: 'column',
          minHeight: 0, flex: 1, position: 'relative', overflow: 'visible',
        }}
      >
        <div style={{ flex: 1, minHeight: MIN_VIZ_H, overflow: 'auto', display: 'flex', minWidth: 0 }}>
          <VizPanel />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={hostRef}
      style={{
        display: 'flex', flexDirection: 'column',
        minHeight: 0, flex: 1, position: 'relative', overflow: 'visible',
      }}
    >
      <div style={{ height: vizH, minHeight: MIN_VIZ_H, overflow: 'auto', display: 'flex', minWidth: 0 }}>
        <VizPanel />
      </div>

      <div
        onPointerDown={tableOpen ? drag.onPointerDown : undefined}
        onPointerMove={tableOpen ? drag.onPointerMove : undefined}
        onPointerUp={drag.onPointerUp}
        style={{
          height: DIVIDER_H,
          margin: '8px 0 6px',
          cursor: tableOpen ? 'row-resize' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          flexShrink: 0,
          position: 'relative',
          zIndex: 5,
        }}
      >
        <div style={{ height: 3, width: 64, borderRadius: 999, background: 'var(--bd-1)' }} />
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); toggleTable() }}
          style={{
            marginLeft: 10,
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 999,
            border: '0.5px solid var(--bd-1)',
            background: tableOpen ? 'var(--bg-2)' : 'transparent',
            color: 'var(--t-1)',
            cursor: 'pointer',
            fontFamily: 'var(--font)',
          }}
        >
          {tableOpen ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {tableOpen && (
        <div
          style={{
            height: tableH,
            minHeight: MIN_TABLE_H,
            overflow: 'hidden',
            flexShrink: 0,
            marginTop: overlapPx ? -overlapPx : 0,
            position: 'relative',
            zIndex: overlapPx ? 3 : 1,
            boxShadow: overlapPx ? '0 -18px 32px rgba(0,0,0,0.55)' : undefined,
          }}
        >
          {detailTable}
        </div>
      )}
    </div>
  )
}